// Designs ("Design Uploads") — retailer's CAD files, renders and plans grouped
// by customer project. Mirrors the source-of-truth at:
//   /var/www/kitchenos/portal-standalone.html
//     page section:    line 2869   (#page-designs)
//     stats helper:    line 9724   (renderDesignsStats)
//     list renderer:   line 9748   (window.renderDesignsPage)
//     card renderer:   line 9807   (designCard)
//     upload modal:    line 9899   (window.designsOpenUpload)
//
// Backend status (as of this extraction):
//   The DB schema HAS `designs` and `design_files` tables (with file_url,
//   visibility, file_type, notes — see migrations/0001_initial_schema.sql:517-550)
//   but kitchenos-api/server.js exposes NO retailer-portal CRUD routes for
//   them. customer_portal.js only emits a demo `designs:[]` array embedded in
//   the customer project payload.
//
// We therefore call the *intended* retailer endpoints below and gracefully
// 404-fall-back to an empty list / local-only optimistic mutations so the
// page renders cleanly today and switches to live data once the routes ship.
//
// Upload is intentionally STUBBED (no multipart endpoint exists). Clicking
// "Upload design" opens a metadata-only modal that surfaces a friendly
// "Upload not yet wired" toast on save — this matches the master-doc spec
// (we still expose all the metadata fields the eventual endpoint will need).

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup } from "@tkos/shared-ui";
import { openLightbox, type LightboxImage } from "../../shell/lightbox";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

/** SQL CHECK: file_type IN ('png','jpg','jpeg','pdf','dwg','svg','webp'). */
const FILE_TYPES = ["png", "jpg", "jpeg", "pdf", "dwg", "svg", "webp"] as const;
type FileType = (typeof FILE_TYPES)[number];

/** SQL CHECK: visibility IN ('hidden','shared'). */
const VISIBILITIES = ["hidden", "shared"] as const;
type Visibility = (typeof VISIBILITIES)[number];

const VIS_FILTERS = ["all", "hidden", "shared"] as const;
type VisibilityFilter = (typeof VIS_FILTERS)[number];

/** Server-side shape we expect from the (future) design files endpoint. */
interface ServerDesignFile {
  id: string;
  /** Either filename or name; tolerate both. */
  filename?: string | null;
  name?: string | null;
  customer?: string | null;
  /** Customer display name (may be embedded under customer_name). */
  customer_name?: string | null;
  project?: string | null;
  file_type?: string | null;
  /** Some legacy payloads may use `ext`. */
  ext?: string | null;
  file_url?: string | null;
  file_size?: number | null;
  size?: number | null;
  notes?: string | null;
  note?: string | null;
  visibility?: string | null;
  uploaded_at?: string | number | null;
  added?: string | number | null;
}

/** Normalised in-memory shape used by this page. */
interface DesignFile {
  id: string;
  name: string;
  customer: string;
  project: string;
  fileType: FileType;
  fileUrl: string;
  size: number;
  note: string;
  visibility: Visibility;
  uploadedAtMs: number;
}

/** Data captured by the upload modal. */
interface UploadForm {
  customer: string;
  project: string;
  note: string;
  visibility: Visibility;
  filename: string;
  fileType: FileType;
}

// ── Constants ────────────────────────────────────────────────────────────────

const VIS_FILTER_OPTIONS: ReadonlyArray<{ value: VisibilityFilter; label: string }> = [
  { value: "all", label: "All visibility" },
  { value: "hidden", label: "Hidden only" },
  { value: "shared", label: "Shared only" },
];

// SVG icons per file type (stroke colour is set via the `data-ext` attribute
// in CSS). Keeping the path in JS so we don't rely on a sprite sheet.
const FILE_ICON_PATHS: Record<FileType, string> = {
  pdf: `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>`,
  dwg: `<path d="M3 3h7v7H3z"/><path d="M14 3h7v5h-7z"/><path d="M14 12h7v9h-7z"/><path d="M3 14h7v7H3z"/>`,
  jpg: `<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>`,
  jpeg: `<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>`,
  png: `<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>`,
  svg: `<path d="M12 2v20M2 12h20"/><circle cx="12" cy="12" r="9"/>`,
  webp: `<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>`,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  return dateFmt.format(new Date(ms));
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function extFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

const VALID_TYPE_SET = new Set<FileType>(FILE_TYPES);

function coerceFileType(raw: unknown, fallbackName = ""): FileType {
  if (typeof raw === "string") {
    const lc = raw.toLowerCase();
    if (VALID_TYPE_SET.has(lc as FileType)) return lc as FileType;
  }
  // Fall back to filename's extension if file_type is missing/unknown.
  const ext = extFromName(fallbackName);
  if (VALID_TYPE_SET.has(ext as FileType)) return ext as FileType;
  return "pdf";
}

function coerceVisibility(raw: unknown): Visibility {
  if (typeof raw === "string") {
    const lc = raw.toLowerCase();
    if (lc === "shared" || lc === "visible" || lc === "true") return "shared";
    if (lc === "hidden" || lc === "false") return "hidden";
  }
  if (typeof raw === "boolean") return raw ? "shared" : "hidden";
  return "hidden";
}

function coerceWhen(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

function normaliseFile(f: ServerDesignFile): DesignFile {
  const name = String(f.filename ?? f.name ?? "Untitled").trim();
  return {
    id: String(f.id ?? ""),
    name,
    customer: String(f.customer_name ?? f.customer ?? "").trim() || "Unknown customer",
    project: String(f.project ?? "").trim(),
    fileType: coerceFileType(f.file_type ?? f.ext, name),
    fileUrl: String(f.file_url ?? "").trim(),
    size: Number.isFinite(f.file_size as number)
      ? Number(f.file_size)
      : Number.isFinite(f.size as number)
        ? Number(f.size)
        : 0,
    note: String(f.notes ?? f.note ?? "").trim(),
    visibility: coerceVisibility(f.visibility),
    uploadedAtMs: coerceWhen(f.uploaded_at ?? f.added),
  };
}

/** Lightweight HTML escape for static-ish list-card markup. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Mount ────────────────────────────────────────────────────────────────────

const designsPage: Page = {
  name: "designs",
  label: "Designs",
  icon: "🖼",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // ── State ───────────────────────────────────────────────────────────────
    const ac = new AbortController();
    let files: DesignFile[] = [];
    let search = "";
    let customerFilter = "";
    let visFilter: VisibilityFilter = "all";
    let isLoading = true;
    let loadingError: string | null = null;
    /** Stamp updated whenever local state mutates — drives the "All changes saved" indicator. */
    let lastSavedAt = Date.now();

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const root = document.createElement("section");
    root.className = "designs-page";
    root.innerHTML = `
      <header class="designs-header">
        <div class="designs-header__title">
          <div class="designs-header__icon" aria-hidden="true">
            ${iconMarkup("dashboard", { size: "sm" })}
          </div>
          <div>
            <h1>Design Uploads</h1>
            <p>Import CAD files, renders and plans from Winner, ArtiCAD, 2020 Design, ProKitchen and more</p>
          </div>
        </div>
        <span class="designs-saved" data-role="saved" aria-live="polite">
          ${iconMarkup("check", { size: "sm" })}
          All changes saved
        </span>
      </header>

      <div class="designs-toolbar">
        <label class="designs-search">
          ${iconMarkup("search", { size: "sm" })}
          <input type="text" data-role="search" placeholder="Search designs..." aria-label="Search designs by name or customer" />
        </label>
        <select class="designs-select" data-role="customer-filter" aria-label="Filter by customer">
          <option value="">All customers</option>
        </select>
        <select class="designs-select" data-role="vis-filter" aria-label="Filter by visibility">
          ${VIS_FILTER_OPTIONS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
        <span class="designs-toolbar__spacer"></span>
        <button class="designs-btn designs-btn--primary" type="button" data-role="upload">
          ${iconMarkup("upload", { size: "sm" })}
          Upload design
        </button>
      </div>

      <div class="designs-stats" data-role="stats"></div>

      <div class="designs-gallery" data-role="gallery" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const searchEl = root.querySelector<HTMLInputElement>("[data-role='search']")!;
    const customerFilterEl = root.querySelector<HTMLSelectElement>("[data-role='customer-filter']")!;
    const visFilterEl = root.querySelector<HTMLSelectElement>("[data-role='vis-filter']")!;
    const uploadBtn = root.querySelector<HTMLButtonElement>("[data-role='upload']")!;
    const statsEl = root.querySelector<HTMLDivElement>("[data-role='stats']")!;
    const galleryEl = root.querySelector<HTMLDivElement>("[data-role='gallery']")!;
    const savedEl = root.querySelector<HTMLSpanElement>("[data-role='saved']")!;

    // ── Upload modal (metadata-only stub; multipart endpoint TBD) ───────────

    const dialog = document.createElement("dialog");
    dialog.className = "designs-modal";
    dialog.innerHTML = `
      <form method="dialog" class="designs-modal__form" data-role="form" novalidate>
        <header class="designs-modal__head">
          <h2>Upload design files</h2>
          <button class="designs-modal__close" type="button" data-role="close" aria-label="Close">&times;</button>
        </header>
        <div class="designs-modal__body">
          <div class="designs-field">
            <label for="du-customer">Customer project <span class="designs-field__hint">(name or select existing)</span></label>
            <input id="du-customer" name="customer" list="du-customer-list" type="text" autocomplete="off" placeholder="e.g. Sarah &amp; Tom Mitchell" required />
            <datalist id="du-customer-list" data-role="customer-datalist"></datalist>
            <span class="designs-field__error">Customer name is required.</span>
          </div>
          <div class="designs-field">
            <label for="du-project">Project name <span class="designs-field__hint">(optional)</span></label>
            <input id="du-project" name="project" type="text" placeholder="e.g. Mitchell Kitchen" />
          </div>
          <div class="designs-field">
            <label for="du-note">Label / description <span class="designs-field__hint">(optional)</span></label>
            <input id="du-note" name="note" type="text" placeholder="e.g. Final Layout, 3D Render — Option A, Elevation View" />
          </div>
          <div class="designs-field">
            <label>Visibility</label>
            <div class="designs-radio-group" role="radiogroup" aria-label="Visibility">
              <label class="designs-radio">
                <input type="radio" name="visibility" value="hidden" checked />
                <span><strong>Hidden from customer</strong><em>recommended for plans &amp; elevations</em></span>
              </label>
              <label class="designs-radio">
                <input type="radio" name="visibility" value="shared" />
                <span><strong>Shared with customer</strong><em>good for 3D renders</em></span>
              </label>
            </div>
          </div>
          <div class="designs-field">
            <label for="du-file">File</label>
            <input id="du-file" name="file" type="file" accept=".pdf,.jpg,.jpeg,.png,.dxf,.dwg,.svg,.webp,image/*,application/pdf" data-role="file" />
            <span class="designs-field__hint" data-role="file-info">No file selected</span>
          </div>
          <p class="designs-modal__notice" role="note">
            ${iconMarkup("info", { size: "sm" })}
            Hidden designs aren't visible in the customer portal until you choose to share them. Use this to protect detailed plans until the deposit is paid.
          </p>
        </div>
        <footer class="designs-modal__footer">
          <button type="button" class="designs-btn" data-role="cancel">Cancel</button>
          <button type="submit" class="designs-btn designs-btn--primary" data-role="save">Upload</button>
        </footer>
      </form>
    `;
    root.appendChild(dialog);

    const formEl = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
    const customerInput = dialog.querySelector<HTMLInputElement>("#du-customer")!;
    const customerDatalist = dialog.querySelector<HTMLDataListElement>("[data-role='customer-datalist']")!;
    const projectInput = dialog.querySelector<HTMLInputElement>("#du-project")!;
    const noteInput = dialog.querySelector<HTMLInputElement>("#du-note")!;
    const fileInput = dialog.querySelector<HTMLInputElement>("[data-role='file']")!;
    const fileInfoEl = dialog.querySelector<HTMLSpanElement>("[data-role='file-info']")!;
    const customerField = dialog.querySelector<HTMLDivElement>(".designs-field")!;
    const saveBtn = dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const cancelBtn = dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;

    // ── Edit-note modal ─────────────────────────────────────────────────────

    const noteDialog = document.createElement("dialog");
    noteDialog.className = "designs-modal designs-modal--small";
    noteDialog.innerHTML = `
      <form method="dialog" class="designs-modal__form" data-role="note-form" novalidate>
        <header class="designs-modal__head">
          <h2>Edit note</h2>
          <button class="designs-modal__close" type="button" data-role="note-close" aria-label="Close">&times;</button>
        </header>
        <div class="designs-modal__body">
          <div class="designs-field">
            <label for="dn-note">Label / description</label>
            <input id="dn-note" type="text" placeholder="e.g. Final layout — handleless graphite" />
          </div>
        </div>
        <footer class="designs-modal__footer">
          <button type="button" class="designs-btn" data-role="note-cancel">Cancel</button>
          <button type="submit" class="designs-btn designs-btn--primary" data-role="note-save">Save</button>
        </footer>
      </form>
    `;
    root.appendChild(noteDialog);

    const noteFormEl = noteDialog.querySelector<HTMLFormElement>("[data-role='note-form']")!;
    const noteEditInput = noteDialog.querySelector<HTMLInputElement>("#dn-note")!;
    const noteCancelBtn = noteDialog.querySelector<HTMLButtonElement>("[data-role='note-cancel']")!;
    const noteCloseBtn = noteDialog.querySelector<HTMLButtonElement>("[data-role='note-close']")!;
    let editingNoteFor: string | null = null;

    // ── Modal helpers ───────────────────────────────────────────────────────

    function openDialog(d: HTMLDialogElement): void {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    }

    function closeDialog(d: HTMLDialogElement): void {
      if (d.open) d.close();
    }

    function refreshCustomerDatalist(): void {
      const customers = uniqueCustomers();
      customerDatalist.innerHTML = customers
        .map((c) => `<option value="${esc(c)}"></option>`)
        .join("");
    }

    function resetUploadForm(preCustomer = ""): void {
      customerInput.value = preCustomer;
      projectInput.value = "";
      noteInput.value = "";
      fileInput.value = "";
      fileInfoEl.textContent = "No file selected";
      customerField.classList.remove("designs-field--invalid");
      const hiddenRadio = formEl.querySelector<HTMLInputElement>(
        "input[name='visibility'][value='hidden']",
      );
      if (hiddenRadio) hiddenRadio.checked = true;
    }

    function openUploadModal(preCustomer = ""): void {
      refreshCustomerDatalist();
      resetUploadForm(preCustomer);
      openDialog(dialog);
      window.setTimeout(() => customerInput.focus(), 60);
    }

    function openNoteModal(id: string): void {
      const f = files.find((x) => x.id === id);
      if (!f) return;
      editingNoteFor = id;
      noteEditInput.value = f.note;
      openDialog(noteDialog);
      window.setTimeout(() => noteEditInput.focus(), 60);
    }

    // ── Render ──────────────────────────────────────────────────────────────

    function uniqueCustomers(): string[] {
      const set = new Set<string>();
      for (const f of files) {
        if (f.customer) set.add(f.customer);
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    function renderStats(): void {
      const total = files.length;
      const shared = files.filter((f) => f.visibility === "shared").length;
      const hidden = files.filter((f) => f.visibility === "hidden").length;
      const customers = uniqueCustomers().length;
      statsEl.innerHTML = `
        ${statTile(total, "Total designs", "indigo")}
        ${statTile(shared, "Shared with customers", "green")}
        ${statTile(hidden, "Hidden (retailer only)", "red")}
        ${statTile(customers, "Customer projects", "amber")}
      `;
    }

    function statTile(value: number, label: string, hue: string): string {
      return `
        <div class="designs-stat" data-hue="${hue}">
          <strong>${value}</strong>
          <span>${esc(label)}</span>
        </div>
      `;
    }

    function renderCustomerFilter(): void {
      const current = customerFilterEl.value;
      const customers = uniqueCustomers();
      customerFilterEl.innerHTML =
        `<option value="">All customers</option>` +
        customers
          .map(
            (c) =>
              `<option value="${esc(c)}"${c === current ? " selected" : ""}>${esc(c)}</option>`,
          )
          .join("");
      if (current && !customers.includes(current)) {
        customerFilter = "";
        customerFilterEl.value = "";
      }
    }

    function renderGallery(): void {
      if (isLoading) {
        galleryEl.innerHTML = `<div class="designs-state">Loading designs…</div>`;
        return;
      }
      if (loadingError) {
        galleryEl.innerHTML = `<div class="designs-state designs-state--error">${esc(loadingError)}</div>`;
        return;
      }

      const q = search.toLowerCase().trim();
      const filtered = files.filter((f) => {
        if (customerFilter && f.customer !== customerFilter) return false;
        if (visFilter !== "all" && f.visibility !== visFilter) return false;
        if (q) {
          const hay = `${f.name} ${f.customer} ${f.note}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });

      if (filtered.length === 0) {
        galleryEl.innerHTML = `
          <div class="designs-empty">
            ${iconMarkup("dashboard", { size: "sm" })}
            <div class="designs-empty__title">${files.length === 0 ? "No designs yet" : "No designs match your filters"}</div>
            <div class="designs-empty__sub">${files.length === 0 ? "Upload your first design using the button above" : "Try clearing the search or filters"}</div>
          </div>
        `;
        return;
      }

      // Group by customer.
      const groups = new Map<string, DesignFile[]>();
      for (const f of filtered) {
        const arr = groups.get(f.customer);
        if (arr) arr.push(f);
        else groups.set(f.customer, [f]);
      }
      // Sort each group by upload date (newest first), and the group keys alphabetically.
      const customerKeys = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));

      galleryEl.innerHTML = customerKeys
        .map((cust) => {
          const groupFiles = (groups.get(cust) ?? [])
            .slice()
            .sort((a, b) => b.uploadedAtMs - a.uploadedAtMs);
          const count = groupFiles.length;
          return `
            <section class="designs-group">
              <header class="designs-group__head">
                <span class="designs-group__dot" aria-hidden="true"></span>
                <span class="designs-group__name">${esc(cust)}</span>
                <span class="designs-group__count">(${count} file${count !== 1 ? "s" : ""})</span>
                <button class="designs-btn designs-btn--ghost designs-group__add" type="button" data-action="add-file" data-customer="${esc(cust)}">
                  ${iconMarkup("plus", { size: "sm" })}
                  Add file
                </button>
              </header>
              <div class="designs-grid">
                ${groupFiles.map(renderCard).join("")}
              </div>
            </section>
          `;
        })
        .join("");
    }

    // Which design files render as an inline image thumb (and are therefore
    // lightbox-viewable). Kept in one place so renderCard and the click handler
    // agree on the set that prev/next cycles.
    function isThumbImage(f: DesignFile): boolean {
      const ext = f.fileType;
      return (ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "webp") && !!f.fileUrl;
    }

    function renderCard(f: DesignFile): string {
      const ext = f.fileType;
      const iconSvg = `<svg viewBox="0 0 24 24" aria-hidden="true">${FILE_ICON_PATHS[ext] ?? FILE_ICON_PATHS.pdf}</svg>`;
      const isImage = isThumbImage(f);
      const thumb =
        isImage && f.fileUrl
          ? `<img src="${esc(f.fileUrl)}" alt="${esc(f.name)}" loading="lazy" />`
          : `<div class="designs-card__placeholder" data-ext="${ext}">${iconSvg}<span>${esc(ext.toUpperCase())}</span></div>`;
      // Image thumbs open in the in-portal lightbox (prev/next cycles this
      // customer's images). Marked on the thumb wrapper below.
      const viewAttr = isImage && f.fileUrl ? ` data-action="view" data-id="${esc(f.id)}"` : "";
      const visClass = f.visibility === "shared" ? "is-shared" : "is-hidden";
      const visLabel = f.visibility === "shared" ? "Shared" : "Hidden";
      const visTitle =
        f.visibility === "shared"
          ? "Visible to customer — click to hide"
          : "Hidden from customer — click to share";
      const noteHtml = f.note
        ? `<div class="designs-card__note">${esc(f.note)}</div>`
        : "";

      return `
        <article class="designs-card" data-id="${esc(f.id)}">
          <div class="designs-card__thumb${viewAttr ? " is-viewable" : ""}"${viewAttr}>
            ${thumb}
            <span class="designs-card__type" data-ext="${ext}">${esc(ext.toUpperCase())}</span>
            <button type="button" class="designs-card__vis ${visClass}" data-action="toggle-vis" data-id="${esc(f.id)}" title="${esc(visTitle)}">
              ${iconMarkup(f.visibility === "shared" ? "eye" : "eye-off", { size: "sm" })}
              ${visLabel}
            </button>
          </div>
          <div class="designs-card__body">
            <div class="designs-card__name">${esc(f.name)}</div>
            <div class="designs-card__meta">
              <span>${esc(formatDate(f.uploadedAtMs))}</span>
              <span>·</span>
              <span>${esc(formatSize(f.size))}</span>
            </div>
            ${noteHtml}
          </div>
          <div class="designs-card__footer">
            <button type="button" class="designs-btn designs-btn--ghost" data-action="edit-note" data-id="${esc(f.id)}">
              ${iconMarkup("pencil", { size: "sm" })}
              Edit note
            </button>
            <button type="button" class="designs-btn designs-btn--ghost" data-action="quote" data-id="${esc(f.id)}">
              ${iconMarkup("pound", { size: "sm" })}
              Quote
            </button>
            <button type="button" class="designs-btn designs-btn--ghost designs-btn--danger" data-action="delete" data-id="${esc(f.id)}">
              ${iconMarkup("trash", { size: "sm" })}
              Delete
            </button>
          </div>
        </article>
      `;
    }

    function renderAll(): void {
      renderStats();
      renderCustomerFilter();
      renderGallery();
      // Subtle "Saved" indicator: pulse class on every state change.
      savedEl.dataset.stamp = String(lastSavedAt);
    }

    // ── Backend (stubbed where missing) ─────────────────────────────────────

    async function loadFiles(): Promise<void> {
      isLoading = true;
      loadingError = null;
      renderAll();

      // TODO: real endpoint — once backend ships:
      //   GET /api/portal/design-files
      //     -> { files: ServerDesignFile[] }
      // Schema is at migrations/0001_initial_schema.sql:531 (design_files).
      try {
        const resp = await ctx.api<{ files?: ServerDesignFile[]; designs?: ServerDesignFile[] }>(
          "/portal/design-files",
          { method: "GET", signal: ac.signal },
        );
        const list = resp?.files ?? resp?.designs ?? [];
        files = list.map(normaliseFile);
      } catch (err) {
        if (isAbortError(err)) return;
        if (err instanceof ApiError && err.status === 404) {
          console.info(
            "[designs] /api/portal/design-files not implemented yet; rendering empty state.",
          );
          files = [];
        } else {
          console.error("[designs] load failed", err);
          loadingError = "Could not load designs. Please try again.";
          ctx.showToast("Could not load designs", "error");
        }
      } finally {
        isLoading = false;
        renderAll();
      }
    }

    async function toggleVisibility(id: string): Promise<void> {
      const idx = files.findIndex((f) => f.id === id);
      if (idx < 0) return;
      const cur = files[idx];
      if (!cur) return;
      const next: Visibility = cur.visibility === "shared" ? "hidden" : "shared";

      // TODO: real endpoint:
      //   PATCH /api/portal/design-files/:id  body: { visibility }
      try {
        await ctx.api<unknown>(
          `/portal/design-files/${encodeURIComponent(id)}`,
          {
            method: "PATCH",
            signal: ac.signal,
            body: JSON.stringify({ visibility: next }),
          },
        );
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[designs] toggleVis failed", err);
          ctx.showToast("Could not update visibility", "error");
          return;
        }
        // 404 — backend not ready, fall through to local-only update.
        console.info("[designs] PATCH not implemented; toggling locally only.");
      }
      files[idx] = { ...cur, visibility: next };
      lastSavedAt = Date.now();
      ctx.showToast(
        next === "shared"
          ? "Design shared — visible in customer portal"
          : "Design hidden from customer",
        "info",
      );
      renderAll();
    }

    async function saveNote(id: string, newNote: string): Promise<void> {
      const idx = files.findIndex((f) => f.id === id);
      if (idx < 0) return;
      const cur = files[idx];
      if (!cur) return;

      // TODO: real endpoint:
      //   PATCH /api/portal/design-files/:id  body: { notes }
      try {
        await ctx.api<unknown>(
          `/portal/design-files/${encodeURIComponent(id)}`,
          {
            method: "PATCH",
            signal: ac.signal,
            body: JSON.stringify({ notes: newNote }),
          },
        );
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[designs] saveNote failed", err);
          ctx.showToast("Could not save note", "error");
          return;
        }
        console.info("[designs] PATCH not implemented; updating note locally only.");
      }
      files[idx] = { ...cur, note: newNote };
      lastSavedAt = Date.now();
      ctx.showToast("Note updated", "success");
      renderAll();
    }

    async function deleteFile(id: string): Promise<void> {
      // TODO: real endpoint:
      //   DELETE /api/portal/design-files/:id
      try {
        await ctx.api<unknown>(
          `/portal/design-files/${encodeURIComponent(id)}`,
          { method: "DELETE", signal: ac.signal },
        );
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[designs] delete failed", err);
          ctx.showToast("Could not delete design", "error");
          return;
        }
        console.info("[designs] DELETE not implemented; removing locally only.");
      }
      files = files.filter((f) => f.id !== id);
      lastSavedAt = Date.now();
      ctx.showToast("Design deleted", "info");
      renderAll();
    }

    async function createFile(form: UploadForm): Promise<void> {
      // TODO: real endpoint — once backend ships:
      //   POST /api/portal/design-files  (multipart/form-data)
      //     fields: customer, project, notes, visibility, file
      // For now we attempt JSON metadata only; on 404 we keep an optimistic
      // in-memory record so the user still sees feedback.
      const optimistic: DesignFile = {
        id: `tmp-${Date.now()}`,
        name: form.filename || "Untitled",
        customer: form.customer,
        project: form.project,
        fileType: form.fileType,
        fileUrl: "",
        size: 0,
        note: form.note,
        visibility: form.visibility,
        uploadedAtMs: Date.now(),
      };

      try {
        const resp = await ctx.api<{ file: ServerDesignFile }>(
          "/portal/design-files",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({
              customer: form.customer,
              project: form.project,
              notes: form.note,
              visibility: form.visibility,
              filename: form.filename,
              file_type: form.fileType,
            }),
          },
        );
        if (resp?.file) {
          files.push(normaliseFile(resp.file));
        } else {
          files.push(optimistic);
        }
        ctx.showToast("Design uploaded", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (err instanceof ApiError && err.status === 404) {
          files.push(optimistic);
          console.info(
            "[designs] POST /api/portal/design-files not implemented; storing optimistically in-memory.",
          );
          ctx.showToast(
            "Upload not yet wired — design recorded locally for now",
            "info",
          );
        } else {
          console.error("[designs] create failed", err);
          ctx.showToast("Could not upload design", "error");
          return;
        }
      }
      lastSavedAt = Date.now();
      renderAll();
    }

    // ── Form submit handlers ────────────────────────────────────────────────

    function handleUploadSubmit(e: Event): void {
      e.preventDefault();
      const customer = customerInput.value.trim();
      if (!customer) {
        customerField.classList.add("designs-field--invalid");
        customerInput.focus();
        return;
      }

      const fileObj = fileInput.files && fileInput.files[0];
      const filename = fileObj?.name ?? "";
      if (!filename) {
        fileInfoEl.textContent = "Please choose a file";
        return;
      }
      const ext = extFromName(filename);
      const fileType = VALID_TYPE_SET.has(ext as FileType)
        ? (ext as FileType)
        : "pdf";

      const visEl = formEl.querySelector<HTMLInputElement>(
        "input[name='visibility']:checked",
      );
      const visibility = coerceVisibility(visEl?.value);

      const form: UploadForm = {
        customer,
        project: projectInput.value.trim(),
        note: noteInput.value.trim(),
        visibility,
        filename,
        fileType,
      };

      saveBtn.disabled = true;
      saveBtn.textContent = "Uploading…";
      void createFile(form).finally(() => {
        saveBtn.disabled = false;
        saveBtn.textContent = "Upload";
        closeDialog(dialog);
      });
    }

    function handleNoteSubmit(e: Event): void {
      e.preventDefault();
      if (!editingNoteFor) {
        closeDialog(noteDialog);
        return;
      }
      const id = editingNoteFor;
      const newNote = noteEditInput.value.trim();
      editingNoteFor = null;
      void saveNote(id, newNote);
      closeDialog(noteDialog);
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    const onSearchInput = (): void => {
      search = searchEl.value;
      renderGallery();
    };
    searchEl.addEventListener("input", onSearchInput);

    const onCustomerFilterChange = (): void => {
      customerFilter = customerFilterEl.value;
      renderGallery();
    };
    customerFilterEl.addEventListener("change", onCustomerFilterChange);

    const onVisFilterChange = (): void => {
      const v = visFilterEl.value as VisibilityFilter;
      visFilter = (VIS_FILTERS as readonly string[]).includes(v) ? v : "all";
      renderGallery();
    };
    visFilterEl.addEventListener("change", onVisFilterChange);

    const onUploadClick = (): void => openUploadModal();
    uploadBtn.addEventListener("click", onUploadClick);

    const onFileChange = (): void => {
      const f = fileInput.files && fileInput.files[0];
      if (!f) {
        fileInfoEl.textContent = "No file selected";
      } else {
        fileInfoEl.textContent = `${f.name} · ${formatSize(f.size)}`;
      }
    };
    fileInput.addEventListener("change", onFileChange);

    formEl.addEventListener("submit", handleUploadSubmit);
    cancelBtn.addEventListener("click", () => closeDialog(dialog));
    closeBtn.addEventListener("click", () => closeDialog(dialog));

    noteFormEl.addEventListener("submit", handleNoteSubmit);
    noteCancelBtn.addEventListener("click", () => {
      editingNoteFor = null;
      closeDialog(noteDialog);
    });
    noteCloseBtn.addEventListener("click", () => {
      editingNoteFor = null;
      closeDialog(noteDialog);
    });

    // Click-outside-to-close on the upload modal.
    const onDialogClick = (e: MouseEvent): void => {
      if (e.target === dialog) closeDialog(dialog);
    };
    dialog.addEventListener("click", onDialogClick);

    const onNoteDialogClick = (e: MouseEvent): void => {
      if (e.target === noteDialog) {
        editingNoteFor = null;
        closeDialog(noteDialog);
      }
    };
    noteDialog.addEventListener("click", onNoteDialogClick);

    // Gallery event delegation — toggle-vis / edit-note / quote / delete /
    // add-file (per-customer add).
    const onGalleryClick = (e: MouseEvent): void => {
      // Image thumb → open the lightbox over this customer's images.
      const thumb = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        ".designs-card__thumb.is-viewable",
      );
      if (thumb) {
        const id = thumb.dataset.id;
        const f = files.find((x) => x.id === id);
        if (!f) return;
        const groupImgs = files
          .filter((x) => x.customer === f.customer && isThumbImage(x))
          .sort((a, b) => b.uploadedAtMs - a.uploadedAtMs);
        const images: LightboxImage[] = groupImgs.map((x) => ({ url: x.fileUrl as string, name: x.name }));
        openLightbox({ images, startIndex: Math.max(0, groupImgs.findIndex((x) => x.id === id)) });
        return;
      }

      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "button[data-action]",
      );
      if (!btn) return;
      const action = btn.dataset.action;

      if (action === "add-file") {
        const cust = btn.dataset.customer ?? "";
        openUploadModal(cust);
        return;
      }

      const id = btn.dataset.id;
      if (!id) return;

      if (action === "toggle-vis") {
        void toggleVisibility(id);
      } else if (action === "edit-note") {
        openNoteModal(id);
      } else if (action === "quote") {
        const f = files.find((x) => x.id === id);
        if (!f) return;
        // Quote builder is a separate page (registered as `quote-builder` per
        // shell convention, used by the quote-builder agent). Navigate; the
        // builder will read selection state from its own URL/store. If that
        // page isn't registered yet, surface a friendly toast.
        try {
          ctx.navigate("quote-builder");
          ctx.showToast(`Starting quote for ${f.customer}`, "info");
        } catch {
          ctx.showToast("Quote builder not available yet", "info");
        }
      } else if (action === "delete") {
        const f = files.find((x) => x.id === id);
        if (!f) return;
        void (async () => {
          if (!(await confirmDialog(`Delete "${f.name}"? This cannot be undone.`, {
            title: "Delete design",
            confirmLabel: "Delete",
            destructive: true,
          }))) return;
          void deleteFile(id);
        })();
      }
    };
    galleryEl.addEventListener("click", onGalleryClick);

    // ── Initial render & load ───────────────────────────────────────────────

    renderAll();
    void loadFiles();

    // ── Cleanup ─────────────────────────────────────────────────────────────
    return () => {
      ac.abort();
      searchEl.removeEventListener("input", onSearchInput);
      customerFilterEl.removeEventListener("change", onCustomerFilterChange);
      visFilterEl.removeEventListener("change", onVisFilterChange);
      uploadBtn.removeEventListener("click", onUploadClick);
      fileInput.removeEventListener("change", onFileChange);
      formEl.removeEventListener("submit", handleUploadSubmit);
      noteFormEl.removeEventListener("submit", handleNoteSubmit);
      dialog.removeEventListener("click", onDialogClick);
      noteDialog.removeEventListener("click", onNoteDialogClick);
      galleryEl.removeEventListener("click", onGalleryClick);
      if (dialog.open) dialog.close();
      if (noteDialog.open) noteDialog.close();
    };
  },
};

registerPage(designsPage);

export default designsPage;
